import React, { useMemo } from 'react';
import { css, Theme, useTheme } from "@emotion/react";

import { NOOP } from "core/constants";
import { isBetween, isOnly, isUp } from "theme/breakpoints";
import useBreakpoint from "core/hooks/useBreakpoint";

type Props = {
    optionLabel: React.ReactNode,
    Icon: React.ElementType,
    onSelection: (value: string) => void,
    isSelected: boolean,
    leftBubbleDirection?: boolean,
    iconHeight?: number,
    iconWidth?: number,
    key: string,
    hasCircle?: boolean,
    className?: string,
}

const OptionCard = (
    {
        key,
        optionLabel,
        Icon,
        onSelection = NOOP,
        isSelected = false,
        leftBubbleDirection = false,
        iconHeight = 0,
        iconWidth = 0,
        hasCircle = true,
        className = ""
    }: Props
) => {
    const theme: Theme = useTheme();
    const { breakpoint } = useBreakpoint();

    const [
        cardWidth,
        cardHeight,
        topBubbleSize,
        middleBubbleSize,
        bottomBubbleSize,
        outerCircleSize,
        innerCircleSize,
        bubbleContainerWidth,
        bubbleContainerHeight,
        paddingRight,
        paddingLeft,
    ] = useMemo(() => {
        switch (true) {
            case (isUp(breakpoint, 'xxl')):
                return [279, 284, 77, 135, 36, 44, 29, 175, 173, 40, 14];
            case (isOnly(breakpoint, 'xl')):
                return [264, 268, 73, 128, 34, 41, 28, 175, 173, 30, 12];
            case (isBetween(breakpoint, 'md', 'lg')):
                return [229, 232, 63, 111, 30, 35, 24, 145, 140, 10, 10];
            case (isOnly(breakpoint, 'sm')):
                return [194, 198, 54, 94, 25, 30, 20, 120, 130, 20, 8];
            default:
                return [324, 125, 38, 67, 18, 30, 20, 80, 95, 20, 14];
        }
    }, [breakpoint]);

    const containerStyle = css`
        width: ${cardWidth}px;
        height: ${cardHeight}px;
        background: white;
        box-shadow: ${theme.borderAndShadow.boxShadow3};
        border-radius: 20px;
        border: 4px solid transparent;
        cursor: pointer;

        padding: 10px ${paddingRight}px 49px ${paddingLeft}px;

        ${isSelected ? css`
            border: 4px solid ${theme.colours.curiousBlue2};
        ` : css`
            :hover {
                border: 4px solid ${theme.colours.pattensBlue3};
            }
        `}
        ${theme.breakpoints.down('xs')} {
            position: relative;
            padding: 0 0 0 14px;
            width: 100%;
        }
    `;

    const topRowStyle = css`
        display: flex;

        ${theme.breakpoints.down('xs')} {
            height: 100%;
            align-items: center;
        }
    `;

    const outerCircleStyle = css`
        position: relative;
        width: ${outerCircleSize}px;
        height: ${outerCircleSize}px;
        border-radius: 50%;
        background: white;
        border: 3px solid ${theme.colours.curiousBlue2};
        margin-top: 6px;
        margin-right: 15px;

        ${theme.breakpoints.between('md', 'lg')} {
            margin-right: 10px;
        }
    `;

    const innerCircleStyle = css`
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        width: ${innerCircleSize}px;
        height: ${innerCircleSize}px;
        background: ${theme.colours.curiousBlue2};
        border-radius: 50%;
    `;

    const bubblesContainerStyle = css`
        position: relative;
        width: ${bubbleContainerWidth ? `${bubbleContainerWidth}px` : '100%'};
        height: ${bubbleContainerHeight}px;
        margin-left: auto;
        margin-right: auto;
        margin-top: 14px;

        ${theme.breakpoints.down('xs')} {
            position: absolute;
            top: 60%;
            right: 5%;
            transform: translate(-5%, -50%);
            margin: 0;
        }
    `;

    const topBubbleStyle = css`
        position: absolute;
        top: 0;
        ${leftBubbleDirection ? 'left' : 'right'}: 0;
        width: ${topBubbleSize}px;
        height: ${topBubbleSize}px;
        border-radius: 50%;
        background: ${theme.colours[leftBubbleDirection ? 'carnationPink' : 'conifer2']};
    `;

    const middleBubbleStyle = css`
        position: absolute;
        top: 45%;
        left: ${leftBubbleDirection ? 55 : 45}%;
        transform: translate(-50%, -50%);
        width: ${middleBubbleSize}px;
        height: ${middleBubbleSize}px;
        border-radius: 50%;
        background: ${theme.colours.curiousBlue2};
    `;

    const bottomBubbleStyle = css`
        position: absolute;
        ${leftBubbleDirection ? 'right' : 'left'}: 5%;
        bottom: 18%;
        width: ${bottomBubbleSize}px;
        height: ${bottomBubbleSize}px;
        border-radius: 50%;
        background: ${theme.colours[leftBubbleDirection ? 'pizazz' : 'gold']};
    `;

    const iconStyle = css`
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        fill: white;

        ${iconHeight && css`
            height: ${iconHeight}px;
        `}

        ${iconWidth && css`
            width: ${iconWidth}px;
        `}
    `;

    return (
        <div css={containerStyle} onClick={() => onSelection(key)} className={className}>
            <div css={topRowStyle}>
                {hasCircle && (
                    <div css={outerCircleStyle}>
                        {isSelected && <div css={innerCircleStyle} />}
                    </div>
                )}
                <div>
                    {optionLabel}
                </div>
            </div>
            <div css={bubblesContainerStyle}>
                <div css={topBubbleStyle} />
                <div css={middleBubbleStyle}>
                    <Icon css={iconStyle} />
                </div>
                <div css={bottomBubbleStyle} />
            </div>
        </div>
    )
};

export default OptionCard;